<template>
    <div class="card card-w-title">
        <h3>
          <Button v-if="hasAdminRole"
                  icon="pi pi-user-plus"
                  @click="associateUserDialog"
                  v-tooltip.bottom="$t('inviteAssociate')"/>
          {{ associatesRoleLabel || associatesRole + 's' }}
          <Button v-if="hasAdminRole"
                  class="p-button-text"
                  icon="pi pi-question"
                  style="float: right;"
                  v-tooltip.left="associatesRoleDescription"/>
        </h3>
        <DataView :value="associates" layout="grid">
            <template #grid="slotProps">
                <div style="padding: .5em" class="p-col-12 p-md-4 p-lg-6 p-xl-3">
                    <Panel style="text-align: center;">
                        <template #header>
                          <span class="p-text-bold" style="display: inline-block; overflow-wrap: break-word; word-break: break-word">{{ cardHeader(slotProps.data) }}</span>
                        </template>

                        <div><br/></div>
                        <Avatar icon="pi pi-user" size="large" shape="circle" />
                        <div><br/></div>

                        <Button icon="pi pi-info" v-tooltip.bottom="$t('education')" @click="showProfile(slotProps.data.id)"/>
                        <Button v-if="hasAdminRole"
                                icon="pi pi-pencil"
                                style="margin-left: 1em;"
                                v-tooltip.bottom="$t('editUser')"
                                class="p-button-warning"
                                @click="editUser(slotProps.data.id)"></Button>
                        <Button v-if="hasAdminRole"
                                icon="pi pi-ban"
                                style="margin-left: 1em;"
                                v-tooltip.bottom="$t('removeUser')"
                                class="p-button-danger"
                                @click="areYouSureDialog(slotProps.data.id)"></Button>
                    </Panel>
                </div>
            </template>

            <template #empty>{{ $t("noAccountsFound", {roleLabel: associatesRoleLabel}) }}</template>
        </DataView>

        <YesNoDialog
                :displayModal.sync="displayDisassociateUserAreYouSureDialog"
                @on-yes="disassociateUser"/>
        <AssociateUserDialog
                :displayModal.sync="displayAssociateUserDialog"
                :role="associatesRole"
                :role-label="associatesRoleLabel"
                :errors="associateUserErrors"
                @email-invite="associateUser"
                @create-guest="createGuestAccount"/>
        <Dialog :visible.sync="displayProfileDialog" :modal="true" :dismissableMask="true" header=" ">
            <div style="width:450px">
                <UserQualifications :user-id="displayProfileUserId"></UserQualifications>
            </div>
        </Dialog>
    </div>
</template>

<script>
    import AssociateUserDialog from "./AssociateUserDialog";
    import YesNoDialog from "@/components/Shared/YesNoDialog";
    import UserQualifications from "@/components/Users/UserQualifications";
    import OrganizationService from "@/service/OrganizationService";
    import {JwtToken} from "@/utils/jwt";

    export default {
        components: {
            AssociateUserDialog,
            UserQualifications,
            YesNoDialog
        },
        props: {
            associates: Array,
            associatesRole: String,
            associatesRoleLabel: String,
            associatesRoleDescription: String,
            organizationId: Number,
            hasAdminRole: Boolean
        },
        model: {
            event: ['associate-added', 'associate-removed']
        },
        service: null,
        created() {
            this.service = new OrganizationService();
        },
        data() {
            return {
                disassociateUserId: null,
                displayAssociateUserDialog: false,
                displayProfileDialog: false,
                displayProfileUserId: null,
                displayDisassociateUserAreYouSureDialog: false,
                associateUserErrors: []
            }
        },
        methods: {
            associateUser(email) {
                this.service
                    .associateUser(this.organizationId, this.associatesRole, email)
                    .then(result => {
                        if (this.associates.some(associate => associate.id === result.data.id)) {
                            this.$toast.add({
                                severity: 'warn',
                                summary: `User is already an associate`,
                                life: 5000
                            });
                        } else {
                            this.$toast.add({severity: 'success', summary: `Associate invited`, life: 3000});
                            this.$emit("associate-added", result.data);
                        }
                    })
                    .catch(error => {
                        this.$toast.add({severity: 'error', summary: error.response.data[0].description, life: 10000});
                        this.associateUserErrors = error.response.data;
                    })
                    .then(() => this.displayAssociateUserDialog = false);
            },
            createGuestAccount(userDetails) {
                this.service.createGuest(this.organizationId, userDetails)
                    .then(result => {
                        this.$toast.add({severity: 'success', summary: `Guest account created`, life: 3000});
                        this.$emit("associate-added", result.data);
                        this.displayAssociateUserDialog = false;
                    })
                    .catch(error => {
                        this.$toast.add({severity: 'error', summary: error.response.data[0].description, life: 10000});
                        this.associateUserErrors = error.response.data;
                    });
            },
            associateUserDialog() {
                this.displayAssociateUserDialog = true;
            },
            disassociateUser() {
                this.service.disassociateUser(this.organizationId, this.disassociateUserId)
                    .then(() => this.$toast.add({severity: 'success', summary: `Associate removed`, life: 3000}))
                    .then(() => this.$emit("associate-removed", this.disassociateUserId));
            },
            areYouSureDialog(userId) {
                this.disassociateUserId = userId;
                this.displayDisassociateUserAreYouSureDialog = true;
            },
            showProfile(userId) {
                this.displayProfileUserId = userId;
                this.displayProfileDialog = true;
            },
            cardHeader(associate) {

                const header = associate.name || associate.email;

                if (associate.id === JwtToken.fromLocalStorage().userId()) {
                    return "(you) " + header;
                }

                return header;
            },
            editUser(userId) {
                this.$router.push({ path: `/profile/${userId}` })
            }
        }
    }
</script>

<i18n lang="yaml" locale="en">
    inviteAssociate: "Invite new member"
    noAccountsFound: "No {roleLabel} accounts found."
    education: "Education"
    removeUser: "Remove User"
    editUser: "Edit User"
</i18n>

<i18n lang="yaml" locale="dk">
    inviteAssociate: "Inviter nyt medlem"
    noAccountsFound: "Der er ingen {roleLabel}-kontoer."
    education: "Uddannelse"
    removeUser: "Fjern bruger"
    editUser: "Rediger bruger"
</i18n>